import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { updateClient } from "@/features/clients/api";
import { TagMultiSelect } from "@/features/clients/components/tag-multi-select";
import type { Client } from "@/features/clients/types";
import { listTags, type Tag } from "@/features/settings/tags-api";
import { ApiError } from "@/lib/api";

/**
 * Client-screen tag card: shows the client's tags as pills (Calm Studio "Tags"
 * mockup) and lets a practitioner add/remove them from the firm's non-archived
 * tag set via the "Manage" control. Saves by PATCHing the full `tag_ids`
 * replace-set.
 */
export function ClientTagsCard({
  client,
  onClientUpdated,
}: {
  client: Client;
  onClientUpdated: (client: Client) => void;
}) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    listTags()
      .then((list) => active && setTags(list.filter((t) => !t.isArchived)))
      .catch(() => active && setTags([]));
    return () => {
      active = false;
    };
  }, []);

  const assigned = client.tags ?? [];
  const selectedIds = assigned.map((t) => t.id);

  async function handleChange(ids: string[]) {
    setSaving(true);
    try {
      const updated = await updateClient(client.id, { tag_ids: ids });
      onClientUpdated(updated);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update tags.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="gap-4 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Tags</h2>
        <TagMultiSelect
          tags={tags}
          selected={selectedIds}
          onChange={(ids) => void handleChange(ids)}
          disabled={saving}
          placeholder="Manage"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {assigned.map((t) => (
          <span
            key={t.id}
            className="bg-accent text-info rounded-full px-3 py-1 text-[13px] font-medium"
          >
            {t.name}
          </span>
        ))}
        {assigned.length === 0 && (
          <span
            title="Use Manage to add tags"
            className="border-input text-muted-foreground flex items-center gap-1.5 rounded-full border border-dashed px-3 py-1.5 text-[13px] font-medium"
          >
            <Plus className="size-3.5" /> Add a tag
          </span>
        )}
      </div>
    </Card>
  );
}
